import { Route } from "@angular/router";
import { initialDataResolver, organizationsResolver } from "app/app.resolvers";
import { NoAuthGuard } from "app/core/auth/guards/noAuth.guard";
import { LayoutComponent } from "app/layout/layout.component";
import { isAuthDavesaGuard } from "./core/auth-davesa/guards/isAuthDavesaguard";
import { PermissionGuard } from "./core/auth-davesa/guards/permission.guard";
import { PdfViewComponent } from "./layout/common/pdf-view/pdf-view.component";
import { ImageViewerComponent } from "./layout/common/image-viewer/image-viewer.component";
import { RoleRedirectGuard } from "app/core/auth-davesa/guards/role-redirect.guard";
import { EulaGuard } from "./core/auth-davesa/guards/eula.guard";
import { PasswordExpiredGuard } from "./core/auth-davesa/guards/password-expired.guard";

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [
  // Redirect empty path to '/example'
  { path: "", pathMatch: "full", canActivate: [RoleRedirectGuard], component: LayoutComponent, data: { layout: "empty" } },

  // Redirect signed-in user to the '/example'
  //
  // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
  // path. Below is another redirection for that path to redirect the user to the desired
  // location. This is a small convenience to keep all main routes together here on this file.
  {
    path: "signed-in-redirect",
    pathMatch: "full",
    canActivate: [RoleRedirectGuard],
    component: LayoutComponent,
    data: { layout: "empty" }
  },



  // Auth routes for guests
  {
    path: "",
    canActivate: [NoAuthGuard],
    canActivateChild: [NoAuthGuard],
    component: LayoutComponent,
    data: {
      layout: "empty",
    },
    children: [
      {
        path: "confirmation-required",
        loadChildren: () =>
          import(
            "app/modules/auth-davesa/davesa-confirmation-required/davesa-confirmation-required.routes"
          ),
      },
      {
        path: "set-password",
        loadChildren: () =>
          import(
            "app/modules/auth-davesa/davesa-set-password/davesa-set-password.routes"
          ),
      },
      {
        /* Alias for delegation-role invitation links */
        path: "invite-delegation-role",
        loadChildren: () =>
          import(
            "app/modules/auth-davesa/davesa-set-password/davesa-set-password.routes"
          ),
      },
      {
        path: "forgot-password",
        loadChildren: () =>
          import(
            "app/modules/auth-davesa/davesa-forgot-password/davesa-forgot-password.routes"
          ),
      },
      {
        path: "reset-password",
        loadChildren: () =>
          import(
            "app/modules/auth-davesa/davesa-reset-password/davesa-reset-password.routes"
          ),
      },
      {
        path: "sign-in",
        loadChildren: () =>
          import(
            "app/modules/auth-davesa/davesa-sign-in/davesa-sign-in.routes"
          ),
      },
      {
        path: "select-user-role",
        loadChildren: () =>
          import(
            "app/modules/auth-davesa/davesa-select-user-role/davesa-select-user-role.routes"
          ),
      },
      {
        path: "2fa-verify",
        loadChildren: () =>
          import(
            "app/modules/auth-davesa/davesa-2fa-verify/davesa-2fa-verify.routes"
          ),
      },
      {
        path: "password-change",
        canActivate: [PasswordExpiredGuard],
        loadChildren: () =>
          import(
            "app/modules/auth-davesa/davesa-password-change/davesa-password-change.routes"
          ),
      },
    ],
  },

  // EULA Acceptance Page (authenticated but no EULA guard)
  {
    path: "eula-acceptance",
    canActivate: [isAuthDavesaGuard],
    component: LayoutComponent,
    data: {
      layout: "empty",
    },
    children: [
      {
        path: "",
        loadComponent: () =>
          import("app/modules/auth-davesa/eula-acceptance/eula-acceptance.component").then(
            (m) => m.EulaAcceptanceComponent
          ),
      },
    ],
  },

  // Auth routes for authenticated users
  {
    path: "",
    canActivate: [isAuthDavesaGuard, EulaGuard],
    canActivateChild: [isAuthDavesaGuard, EulaGuard],
    component: LayoutComponent,
    data: {
      layout: "empty",
    },
    children: [
      {
        path: "set-password",
        loadChildren: () =>
          import(
            "app/modules/auth-davesa/davesa-set-password/davesa-set-password.routes"
          ),
      },
      {
        /* Alias for delegation-role invitation links */
        path: "invite-delegation-role",
        loadChildren: () =>
          import(
            "app/modules/auth-davesa/davesa-set-password/davesa-set-password.routes"
          ),
      },
      {
        path: "sign-out",
        loadChildren: () =>
          import(
            "app/modules/auth-davesa/davesa-sign-out/davesa-sign-out.routes"
          ),
      },
      {
        path: "unlock-session",
        loadChildren: () =>
          import(
            "app/modules/auth-davesa/davesa-unlock-session/davesa-unlock-session.routes"
          ),
      },
    ],
  },

  // Landing routes
  {
    path: "",
    component: LayoutComponent,
    data: {
      layout: "empty",
    },
    children: [
      {
        path: "home",
        loadChildren: () => import("app/modules/landing/home/home.routes"),
      },
    ],
  },

  // No Permission route
  {
    path: "no-permission",
    canActivate: [isAuthDavesaGuard],
    component: LayoutComponent,
    data: {
      layout: "empty",
    },
    children: [
      {
        path: "",
        loadComponent: () => import("app/modules/auth-davesa/davesa-no-permission/no-permission.component").then(m => m.NoPermissionComponent)
      }
    ]
  },

  // Organization routes (no PermissionGuard, no EulaGuard - org users bypass these)
  // Uses organizationsResolver instead of initialDataResolver to avoid calling tenant-specific APIs
  {
    path: "organizations",
    canActivate: [isAuthDavesaGuard],
    canActivateChild: [isAuthDavesaGuard],
    component: LayoutComponent,
    resolve: {
      initialData: organizationsResolver,
    },
    children: [
      {
        path: "",
        loadChildren: () => import("app/modules/davesa/organization/organization.routes"),
      },
    ],
  },

  // Davesa routes
  {
    path: "",
    canActivate: [isAuthDavesaGuard, EulaGuard],
    canActivateChild: [isAuthDavesaGuard, EulaGuard],
    component: LayoutComponent,
    resolve: {
      initialData: initialDataResolver,
    },
    children: [
        // Studies
        {path: 'studies', 
         canActivate: [PermissionGuard],
         children: [
          {path: 'studies-builder', loadChildren: () => import('app/modules/davesa/studies/studies-builder/studies-builder.routes'), data: { breadcrumb: 'Study Builder' }},
          {path: 'study-constructor', loadChildren: () => import('app/modules/davesa/studies/study-constructor/study-constructor.routes'), data: { breadcrumb: 'Study Constructor' }},
        ]
      },
      // Finance
      {path: 'finance', 
       canActivate: [PermissionGuard],
       children: [
          // Study Constructor
          // {path: 'study-constructor', loadChildren: () => import('app/modules/davesa/studies/study-constructor/study-constructor.routes')},
          // Study Builder
          {path: 'finance-admin', loadChildren: () => import('app/modules/davesa/finance/finance-admin/finance-admin.routes'), data: { breadcrumb: 'Study Builder' }},
          {path: 'finance-dashboard', loadChildren: () => import('app/modules/davesa/finance/finance-admin/finance-dashboard/finance-dashboard.routes'), data: { breadcrumb: 'Finance Dashboard' }},
        ]
      },
      // Administration
      {
        path: "administration",
        canActivate: [PermissionGuard],
        children: [
          {
            path: "global-contacts",
            loadChildren: () =>
              import(
                "app/modules/davesa/administration/global-contacts/global-contacts.routes"
              ),
          },
          // {
          //   path: "entities-data",
          //   loadChildren: () =>
          //     import(
          //       "app/modules/davesa/administration/entities-data/entities-data.routes"
          //     ),
          // },
          {
            path: "entity-data",
            loadChildren: () =>
              import(
                "app/modules/davesa/administration/entity-data/entity-data.routes"
              ),
          },
          {
            path: "sites",
            loadChildren: () =>
              import("app/modules/davesa/administration/sites/sites.routes"),
          },
          {
            path: "users",
            loadChildren: () =>
              import("app/modules/davesa/administration/users/users.routes"),
          },
          {
            path: "finance",
            loadChildren: () =>
              import("app/modules/davesa/administration/finance/finance.routes"),
          },
          {
            path: "eisf-templates",
            loadChildren: () =>
              import(
                "app/modules/davesa/administration/eisf-templates/eisf-templates.routes"
              ),
          },
        ],
      },
      // Dashboards
      {
        path: "dashboards",
        canActivate: [PermissionGuard],
        children: [
          {
            path: "clinic",
            loadChildren: () =>
              import("app/modules/davesa/dashboards/clinic/clinic.routes"),
          },
          {
            path: "regulatory",
            loadChildren: () =>
              import(
                "app/modules/davesa/dashboards/regulatory/regulatory.routes"
              ),
          },
          {
            path: "project",
            loadChildren: () =>
              import("app/modules/davesa/dashboards/project/project.routes"),
          },
          {
            path: "analytics",
            loadChildren: () =>
              import(
                "app/modules/davesa/dashboards/analytics/analytics.routes"
              ),
          },
          {
            path: "finance",
            loadChildren: () =>
              import("app/modules/davesa/dashboards/finance/finance.routes"),
          },
          {
            path: "crypto",
            loadChildren: () =>
              import("app/modules/davesa/dashboards/crypto/crypto.routes"),
          },
          {
            path: "administration",
            loadChildren: () =>
              import("app/modules/davesa/dashboards/administration/administration.routes"),
          },
        ],
      },

      // Apps
      {
        path: "apps",
        canActivate: [PermissionGuard],
        children: [
          {
            path: "profile",
            loadChildren: () =>
              import("app/modules/davesa/profile/profile.routes"),
          },
          {path: 'studies', loadChildren: () => import('app/modules/davesa/apps/studies/studies.routes')},
          {
            path: "participants",
            loadChildren: () =>
              import(
                "app/modules/davesa/apps/participants/participants.routes"
              ),
          },
          {
            path: "visits",
            loadChildren: () =>
              import(
                "app/modules/davesa/apps/visits/visits.routes"
              ),
          },
        ],
      },

      // Study Constructor (ng13 migration screens: list/create)
      {path: 'study-constructor', loadChildren: () => import('app/modules/davesa/apps/study-constructor/study-constructor.routes')},

      // Staff signing routes (auth guard required)
      {
        path: "signing",
        children: [
          {
            path: "staff",
            children: [
              {
                path: "sign/:processOid",
                component: LayoutComponent,
                data: {
                  layout: "empty",
                },
                children: [
                  {
                    path: "",
                    loadComponent: () => import("app/modules/davesa/signing/detail/participant-detail.component").then(m => m.ParticipantDetailComponent)
                  }
                ]
              }
            ]
          }
        ]
      },


      // eSource
      {
        path: "esource",
        children: [
          {
            path: "surveyjs-form-builder",
            loadComponent: () =>
              import("app/modules/davesa/esource/surveyjs-form-builder/surveyjs-form-builder.component").then(
                (m) => m.SurveyjsFormBuilderComponent
              ),
          },
        ],
      },

      // User Interface
      {
        path: "ui",
        children: [
          // Icons
          {
            path: "icons",
            loadChildren: () =>
              import("app/modules/davesa/ui/icons/icons.routes"),
          },
        ],
      },
    ],
  },

  // PDF Viewer
  {
    path: "pdf-view",
    canActivate: [isAuthDavesaGuard, EulaGuard],
    canActivateChild: [isAuthDavesaGuard, EulaGuard],
    component: PdfViewComponent,
  },
  // Image Viewer
  {
    path: "image-viewer",
    canActivate: [isAuthDavesaGuard, EulaGuard],
    canActivateChild: [isAuthDavesaGuard, EulaGuard],
    component: ImageViewerComponent,
  },

  // Participant econsents routes (no auth required)
  {
    path: "econsents",
    component: LayoutComponent,
    data: {
      layout: "empty",
    },
    children: [
      {
        path: "",
        loadChildren: () => import("app/modules/davesa/signing/signing.routes"),
      }
    ]
  },

  // Public external-trainer training log signing (no auth — the trainer has no Davesa account)
  {
    path: "training-log/external/:token",
    component: LayoutComponent,
    data: {
      layout: "empty",
    },
    children: [
      {
        path: "",
        loadComponent: () => import("app/modules/davesa/public/training-log-external/training-log-external.component").then(m => m.TrainingLogExternalComponent)
      }
    ]
  },

  // Public eForm sent by email from the Study Builder (no auth — the recipient has no Davesa account)
  {
    path: "eform/external/:token",
    component: LayoutComponent,
    data: {
      layout: "empty",
    },
    children: [
      {
        path: "",
        // Same page as the eText link; formSource picks the API it loads and submits through
        data: { formSource: "eform-external" },
        loadComponent: () => import("app/modules/davesa/public/etext-form-public/etext-form-public.component").then(m => m.EtextFormPublicComponent)
      }
    ]
  },

  // Public eText routes
  {
    path: "etext/:token",
    component: LayoutComponent,
    data: {
      layout: "empty",
    },
    children: [
      {
        path: "",
        loadComponent: () => import("app/modules/davesa/public/etext-form-public/etext-form-public.component").then(m => m.EtextFormPublicComponent)
      }
    ]
  },
];
